import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  ClipboardDocumentListIcon,
  UserGroupIcon,
  BuildingStorefrontIcon,
  RectangleStackIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import {
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  BuildingStorefrontIcon as BuildingStorefrontIconSolid,
  RectangleStackIcon as RectangleStackIconSolid,
  ChartBarIcon as ChartBarIconSolid,
} from '@heroicons/react/24/solid';
import '../styles/comprehensive-responsive-system.css';

const AdminBottomNavigation: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();

  // Adding a class to the body to add padding at the bottom
  useEffect(() => {
    document.body.classList.add('has-bottom-nav');
    return () => {
      document.body.classList.remove('has-bottom-nav');
    };
  }, []);

  if (!user || !user.roles.includes('ROLE_ADMIN')) {
    return null;
  }

  const navItems = [
    {
      path: '/admin/orders',
      label: 'Orders',
      icon: ClipboardDocumentListIcon,
      activeIcon: ClipboardDocumentListIconSolid
    },
    {
      path: '/admin/users',
      label: 'Users',
      icon: UserGroupIcon,
      activeIcon: UserGroupIconSolid
    },
    {
      path: '/admin/vendor-onboarding',
      label: 'Vendors',
      icon: BuildingStorefrontIcon,
      activeIcon: BuildingStorefrontIconSolid
    },
    {
      path: '/admin/api-performance',
      label: 'API Metrics',
      icon: ChartBarIcon,
      activeIcon: ChartBarIconSolid
    }
  ];

  return (
    <div 
      className="fixed bottom-0 left-0 z-[9999] backdrop-blur-xl"
      style={{
        width: '100%',
        height: '67px',
        backgroundColor: theme.background,
        borderTop: `1px solid ${theme.border}`,
      }}
    >
      {/* Navigation container with space-evenly distribution */}
      <div 
        style={{
          display: 'flex',
          justifyContent: 'space-evenly',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          padding: '8px 0'
        }}
      >
        {navItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path === '/admin/orders' && location.pathname.startsWith('/admin/orders'));
          const Icon = isActive ? item.activeIcon : item.icon;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className="flex flex-col items-center justify-center transition-all duration-200 rounded-lg relative group py-responsive-sm px-responsive-sm touch-target"
              style={{
                fontSize: '0.75rem',
                color: isActive ? theme.primary : theme.gray?.[600] || '#666666',
                fontWeight: isActive ? '600' : '400',
                backgroundColor: isActive ? theme.button.secondary.bg : 'transparent'
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = theme.components?.card?.hover || '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <Icon className={`w-6 h-6 mb-1 transition-transform duration-200 ${isActive ? 'scale-110' : 'group-hover:scale-105'}`} />
              <span className="tracking-wide">{item.label}</span>
              {isActive && (
                <div 
                  className="absolute left-2 right-2 h-[2px] rounded-full"
                  style={{ bottom: '-3px', backgroundColor: theme.primary }}
                />
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default AdminBottomNavigation;

